/**
 * Application sidebar. 240px wide on desktop. Carries the Wordmark, the
 * Cmd-K search affordance, the primary navigation (Home / Tasks /
 * Sales), the disabled Backlog group (Intel / Delivery / Finance), and
 * the user footer block with a settings icon shortcut.
 *
 * Reference: spec section 7.1, Claude Design Files README sections
 * 5 and 9, and `source/shell.jsx` for the visual baseline.
 *
 * M2 closes the structural shell:
 *   - Heroicons outline 1.5px on every nav item
 *   - Settings entry surfaces as a footer icon button (matches design)
 *   - Search affordance triggers the Cmd-K command bar via the shared
 *     command-bar store (so the same dialog opens from either path)
 */

import type { ComponentType, SVGProps } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  HomeIcon,
  RectangleStackIcon,
  ArrowTrendingUpIcon,
  LightBulbIcon,
  TruckIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import { Wordmark } from './Wordmark';
import { useCommandBar } from '@/lib/command-bar';
import { cn } from '@/lib/utils';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface NavItem {
  to: string;
  label: string;
  Icon: IconComponent;
  count?: number;
  badge?: number;
}

const PRIMARY: NavItem[] = [
  { to: '/', label: 'Home', Icon: HomeIcon },
  { to: '/tasks', label: 'Tasks', Icon: RectangleStackIcon },
  { to: '/sales', label: 'Sales', Icon: ArrowTrendingUpIcon },
];

const BACKLOG: { label: string; Icon: IconComponent }[] = [
  { label: 'Intel', Icon: LightBulbIcon },
  { label: 'Delivery', Icon: TruckIcon },
  { label: 'Finance', Icon: BanknotesIcon },
];

export function Sidebar() {
  const { location } = useRouterState();
  const openCommandBar = useCommandBar((s) => s.open);

  return (
    <aside
      aria-label="Primary navigation"
      className="flex h-screen w-[240px] flex-col border-r border-mx-grey-800 bg-mx-black px-mx-4 py-mx-5"
    >
      <div className="flex items-center justify-between">
        <Wordmark />
        <span className="mono-label text-mx-grey-mid">V1</span>
      </div>

      {/* Search button shares the command-bar dialog with the global Cmd-K
       * keyboard handler. M12 fills the command bar with real search;
       * M2 only requires that the dialog opens when triggered. */}
      <button
        type="button"
        onClick={openCommandBar}
        aria-label="Open command bar"
        className="mt-mx-5 flex h-9 w-full items-center gap-mx-2 border border-mx-grey-800 bg-mx-grey-850 px-mx-3 text-left text-mx-body-sm text-mx-grey-500 transition-colors duration-mx-default ease-mx-default hover:border-mx-grey-700"
      >
        <MagnifyingGlassIcon className="h-[14px] w-[14px]" strokeWidth={1.6} aria-hidden />
        <span className="flex-1">Search or run</span>
        <span className="mono-label text-mx-grey-mid">⌘K</span>
      </button>

      <nav aria-label="Workspaces" className="mt-mx-5">
        <p className="mono-label mb-mx-2 text-mx-grey-mid">Workspaces</p>
        <ul className="flex flex-col gap-mx-1">
          {PRIMARY.map((item) => {
            const isActive =
              item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'group relative flex h-9 items-center gap-mx-3 pl-mx-3 pr-mx-3 text-mx-body text-mx-grey-400 transition-colors duration-mx-default ease-mx-default hover:bg-mx-grey-900 hover:text-mx-white',
                    isActive && 'bg-mx-grey-900 text-mx-white',
                  )}
                >
                  {isActive && (
                    <span
                      aria-hidden
                      data-testid="active-rail"
                      className="absolute left-0 top-0 h-full w-[2px] bg-mx-orange"
                    />
                  )}
                  <item.Icon className="h-[15px] w-[15px]" strokeWidth={1.5} aria-hidden />
                  <span className="flex-1">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className="bg-mx-orange px-mx-2 py-[2px] text-mx-mono-sm text-mx-white">
                      {item.badge}
                    </span>
                  )}
                  {item.badge === undefined && item.count !== undefined && (
                    <span className="mono-label text-mx-grey-mid">{item.count}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-mx-5" aria-label="Backlog (disabled in phase one)">
        <p className="mono-label mb-mx-2 text-mx-grey-mid">Backlog</p>
        <ul className="flex flex-col gap-mx-1">
          {BACKLOG.map((item) => (
            <li
              key={item.label}
              aria-disabled="true"
              className="flex h-9 items-center gap-mx-3 pl-mx-3 pr-mx-3 text-mx-body text-mx-grey-mid"
            >
              <item.Icon className="h-[15px] w-[15px]" strokeWidth={1.5} aria-hidden />
              <span className="flex-1">{item.label}</span>
              <span aria-hidden>—</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto flex items-center gap-mx-2 border-t border-mx-grey-800 pt-mx-4">
        <span className="flex h-7 w-7 items-center justify-center bg-mx-grey-700 text-mx-mono-sm text-mx-white">
          PC
        </span>
        <span className="flex flex-1 flex-col">
          <span className="text-mx-body-sm text-mx-white">Peter Clowes</span>
          <span className="mono-label text-mx-grey-mid">CEO</span>
        </span>
        <Link
          to="/settings"
          aria-label="Open settings"
          className="flex h-7 w-7 items-center justify-center text-mx-grey-400 transition-colors duration-mx-default ease-mx-default hover:text-mx-white"
        >
          <Cog6ToothIcon className="h-[15px] w-[15px]" strokeWidth={1.5} aria-hidden />
        </Link>
      </div>
    </aside>
  );
}
